import React, { useEffect, useState } from "react";
import { setBreadcrumbItems } from "../../store/actions";
import { connect } from "react-redux";
import Spinners from "components/Common/Spinner";
import { MDBDataTable } from "mdbreact";
import { Link, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as url from "../../helpers/url_helper";
import { toast } from "react-toastify";

const FormList = (props) => {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [formList, setFormList] = useState([]);

  const [responsesModalOpen, setResponsesModalOpen] = useState(false);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [responses, setResponses] = useState([]);
  const [activeForm, setActiveForm] = useState(null);
  const [formFields, setFormFields] = useState([]);

  const [answersModalOpen, setAnswersModalOpen] = useState(false);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [answerDetail, setAnswerDetail] = useState(null);

  const [downloadingId, setDownloadingId] = useState(null);

  const breadcrumbItems = [
    { title: "VUF", link: "#" },
    { title: "Form List", link: "#" },
  ];

  useEffect(() => {
    props.setBreadcrumbItems("Form List", breadcrumbItems);
  }, []);

  useEffect(() => {
    onGetFormList();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    const rawToken = token || authUser?.accessToken;
    if (!rawToken) return {};
    const authToken = rawToken.startsWith("Bearer ")
      ? rawToken
      : `Bearer ${rawToken}`;
    return { Authorization: authToken };
  };

  async function onGetFormList() {
    setLoading(true);
    try {
      const response = await fetch(`${url.VUF_APP_API}forms`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
      });

      const rawResponse = await response.text();
      let responseData = {};
      try {
        responseData = rawResponse ? JSON.parse(rawResponse) : {};
      } catch {
        responseData = {};
      }

      if (!response.ok) {
        throw new Error(
          responseData?.message ||
            responseData?.error ||
            `Failed to fetch forms (HTTP ${response.status})`
        );
      }

      const list =
        responseData?.data ||
        responseData?.forms ||
        responseData?.result ||
        (Array.isArray(responseData) ? responseData : []);

      const mapped = (Array.isArray(list) ? list : []).map((item) => {
        const rawIsActive =
          item?.isActive ??
          item?.form_definition?.isActive;
        return {
          id: item._id || item.id || "",
          title: item.title || "---",
          description: item.description || "---",
          isActive: rawIsActive === undefined ? true : Boolean(rawIsActive),
          createdAt: item.createdAt
            ? new Date(item.createdAt).toLocaleString()
            : "---",
        };
      });

      setFormList(mapped);
    } catch (error) {
      console.log("err", error);
      toast.error(error?.message || "Error fetching forms");
    } finally {
      setLoading(false);
    }
  }

  async function onDeleteForm(id) {
    if (!id) {
      return toast.error("Id not found");
    }
    if (!window.confirm("Are you sure you want to delete this form?")) return;

    setLoading(true);
    try {
      const response = await fetch(`${url.VUF_APP_API}forms/${id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Failed to delete (HTTP ${response.status})`);
      }

      toast.success("Form deleted successfully");
      onGetFormList();
    } catch (error) {
      console.log("err", error);
      toast.error(error?.message || "Error deleting form");
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadResponses(formId, formTitle) {
    if (!formId) {
      return toast.error("Form id not found");
    }
    setDownloadingId(formId);
    try {
      const response = await fetch(
        `${url.VUF_APP_API}forms/${formId}/responses/download`,
        {
          method: "GET",
          // binary download: do NOT send Accept/Content-Type: application/json
          headers: {
            ...getAuthHeader(),
          },
        }
      );

      if (!response.ok) {
        // server may return a JSON error body instead of the file
        let message = `Failed to download (HTTP ${response.status})`;
        try {
          const errJson = await response.json();
          message = errJson?.message || errJson?.error || message;
        } catch {
          // ignore — body wasn't JSON
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      // backend already sets Content-Disposition; this is just a fallback name
      a.download = `${formTitle || "form-responses"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download failed", error);
      toast.error(error?.message || "Failed to download responses");
    } finally {
      setDownloadingId(null);
    }
  }

  async function openResponsesModal(form) {
    setActiveForm(form);
    setResponses([]);
    setFormFields([]);
    setResponsesModalOpen(true);
    setResponsesLoading(true);
    try {
      const [respRes, formRes] = await Promise.all([
        fetch(`${url.VUF_APP_API}forms/${form.id}/responses`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...getAuthHeader(),
          },
        }),
        fetch(`${url.VUF_APP_API}forms/${form.id}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...getAuthHeader(),
          },
        }),
      ]);

      const respJson = await respRes.json().catch(() => ({}));
      if (!respRes.ok) {
        throw new Error(
          respJson?.message ||
            respJson?.error ||
            `Failed to fetch responses (HTTP ${respRes.status})`
        );
      }

      const list =
        respJson?.data?.responses ||
        respJson?.responses ||
        respJson?.data ||
        [];
      setResponses(Array.isArray(list) ? list : []);

      if (formRes.ok) {
        const formJson = await formRes.json().catch(() => ({}));
        const formData =
          formJson?.data || formJson?.form || formJson?.result || formJson;
        const definition = formData?.form_definition || formData || {};
        setFormFields(
          Array.isArray(definition.fields) ? definition.fields : []
        );
      }
    } catch (error) {
      toast.error(error?.message || "Error fetching responses");
    } finally {
      setResponsesLoading(false);
    }
  }

  function closeResponsesModal() {
    setResponsesModalOpen(false);
    setResponses([]);
    setActiveForm(null);
    setFormFields([]);
  }

  const getResponseId = (r) =>
    r?._id || r?.id || r?.responseId || r?.response_id || "";

  async function openAnswersModal(response) {
    const responseId = getResponseId(response);
    if (!activeForm?.id || !responseId) {
      console.warn("Response object missing id:", response);
      toast.error("Missing response id");
      return;
    }
    setAnswerDetail(null);
    setAnswersModalOpen(true);
    setAnswersLoading(true);
    try {
      const res = await fetch(
        `${url.VUF_APP_API}forms/${activeForm.id}/responses/${responseId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...getAuthHeader(),
          },
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          json?.message ||
            json?.error ||
            `Failed to fetch response (HTTP ${res.status})`
        );
      }
      const detail = json?.data || json?.response || json;
      setAnswerDetail(detail);
    } catch (error) {
      toast.error(error?.message || "Error fetching response detail");
      setAnswersModalOpen(false);
    } finally {
      setAnswersLoading(false);
    }
  }

  function closeAnswersModal() {
    setAnswersModalOpen(false);
    setAnswerDetail(null);
  }

  const formatDateTime = (val) => {
    if (!val) return "---";
    const d = new Date(val);
    return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
  };

  const getUserDisplayName = (responseItem) => {
    const u = responseItem?.user || {};
    return u.fullName || u.email || "Unknown user";
  };

  const getUserInfo = (responseItem) => {
    const u = responseItem?.user || responseItem || {};
    return {
      fullName: u.fullName || u.name || u.email || "Unknown user",
      vufId:
        u.vufId ||
        u.VUFID ||
        u.vuf_id ||
        u.vufID ||
        u.userId ||
        u.user_id ||
        "-",
      mobile:
        u.mobileNo ||
        u.mobile_no ||
        u.mobile ||
        u.mobileNumber ||
        u.mobile_number ||
        u.phone ||
        u.phoneNumber ||
        u.phone_number ||
        "-",
    };
  };

  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const extractUrl = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") {
      return (
        val.url ||
        val.uri ||
        val.src ||
        val.href ||
        val.fileUrl ||
        val.file_url ||
        val.path ||
        val.location ||
        val.downloadUrl ||
        val.download_url ||
        ""
      );
    }
    return "";
  };

  const extractFileName = (val, fallback) => {
    if (val && typeof val === "object") {
      const name =
        val.name ||
        val.fileName ||
        val.filename ||
        val.originalName ||
        val.original_name ||
        "";
      if (name) return String(name);
    }
    const url = typeof val === "string" ? val : extractUrl(val);
    if (url) {
      try {
        const clean = url.split("?")[0].split("#")[0];
        const last = clean.substring(clean.lastIndexOf("/") + 1);
        if (last) return decodeURIComponent(last);
      } catch {
        // ignore
      }
    }
    return fallback || "File";
  };

  const prettyLabelFromKey = (key) => {
    const fixes = {
      cource: "Course",
      course: "Course",
      university: "University",
      college: "College",
      institute: "Institute",
      institution: "Institution",
      stream: "Stream",
      branch: "Branch",
      degree: "Degree",
    };
    const k = String(key || "");
    if (fixes[k.toLowerCase()]) return fixes[k.toLowerCase()];
    const spaced = k
      .replace(/[_-]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  const isImageUrl = (u) =>
    /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|#|$)/i.test(String(u || ""));

  const renderAnswerValueAsHtml = (answer, fieldDef) => {
    const type = answer?.type || fieldDef?.type;
    const inputType = answer?.inputType || fieldDef?.inputType;
    const options = answer?.options || fieldDef?.options || [];
    const rawValue =
      answer?.value !== undefined ? answer.value : answer?.answer;
    const emptyMark = '<span style="color:#999;">---</span>';
    if (
      rawValue === null ||
      rawValue === undefined ||
      rawValue === "" ||
      (Array.isArray(rawValue) && rawValue.length === 0) ||
      (typeof rawValue === "object" &&
        !Array.isArray(rawValue) &&
        Object.keys(rawValue).length === 0)
    ) {
      return emptyMark;
    }
    if (type === "selection") {
      const lookup = (v) => {
        const opt = options.find((o) => String(o.value) === String(v));
        return opt?.label ?? String(v);
      };
      const out = Array.isArray(rawValue)
        ? rawValue.map(lookup).join(", ")
        : lookup(rawValue);
      return escapeHtml(out);
    }
    if (type === "boolean") return rawValue ? "Yes" : "No";
    if (type === "date") {
      const d = new Date(rawValue);
      return escapeHtml(
        isNaN(d.getTime()) ? String(rawValue) : d.toLocaleDateString()
      );
    }
    if (type === "time") {
      const d = new Date(rawValue);
      if (!isNaN(d.getTime()) && String(rawValue).includes("T")) {
        return escapeHtml(d.toLocaleTimeString());
      }
      return escapeHtml(String(rawValue));
    }
    if (type === "image" || inputType === "image") {
      const items = Array.isArray(rawValue) ? rawValue : [rawValue];
      const imgs = items
        .map((it) => extractUrl(it))
        .filter(Boolean)
        .map(
          (u) =>
            `<img src="${escapeHtml(u)}" alt="" style="max-width:220px;max-height:220px;border:1px solid #ddd;border-radius:6px;margin:2px;" crossorigin="anonymous" />`
        )
        .join("");
      return imgs || emptyMark;
    }
    if (type === "file") {
      const items = Array.isArray(rawValue) ? rawValue : [rawValue];
      const parts = items
        .map((it) => {
          const u = extractUrl(it);
          const name = extractFileName(it);
          if (!u && !name) return "";
          if (u && isImageUrl(u)) {
            return `<div style="margin:2px 0;"><a href="${escapeHtml(u)}" target="_blank" rel="noreferrer"><img src="${escapeHtml(u)}" alt="${escapeHtml(name)}" style="max-width:220px;max-height:220px;border:1px solid #ddd;border-radius:6px;" crossorigin="anonymous" /></a></div>`;
          }
          if (u) {
            return `<div style="margin:2px 0;"><a href="${escapeHtml(u)}" target="_blank" rel="noreferrer">${escapeHtml(name)}</a></div>`;
          }
          return `<div style="margin:2px 0;">${escapeHtml(name)}</div>`;
        })
        .filter(Boolean)
        .join("");
      return parts || emptyMark;
    }
    if (type === "organization") {
      if (rawValue && typeof rawValue === "object" && !Array.isArray(rawValue)) {
        const rows = Object.entries(rawValue)
          .filter(([, v]) => v !== null && v !== undefined && v !== "")
          .map(
            ([k, v]) =>
              `<div style="margin:2px 0;"><strong>${escapeHtml(prettyLabelFromKey(k))}:</strong> ${escapeHtml(String(v))}</div>`
          )
          .join("");
        return rows || emptyMark;
      }
    }
    if (type === "multi_text" || Array.isArray(rawValue)) {
      const arr = Array.isArray(rawValue) ? rawValue : [rawValue];
      const parts = arr
        .map((v) => {
          if (v === null || v === undefined || v === "") return "";
          if (typeof v === "object") {
            const rows = Object.entries(v)
              .filter(([, vv]) => vv !== null && vv !== undefined && vv !== "")
              .map(
                ([k, vv]) =>
                  `<strong>${escapeHtml(prettyLabelFromKey(k))}:</strong> ${escapeHtml(String(vv))}`
              )
              .join(" | ");
            return rows ? `<div style="margin:2px 0;">${rows}</div>` : "";
          }
          return `<div style="margin:2px 0;">${escapeHtml(String(v))}</div>`;
        })
        .filter(Boolean)
        .join("");
      return parts || emptyMark;
    }
    if (typeof rawValue === "object") {
      const rows = Object.entries(rawValue)
        .filter(([, v]) => v !== null && v !== undefined && v !== "")
        .map(
          ([k, v]) =>
            `<div style="margin:2px 0;"><strong>${escapeHtml(prettyLabelFromKey(k))}:</strong> ${escapeHtml(typeof v === "object" ? JSON.stringify(v) : String(v))}</div>`
        )
        .join("");
      return rows || emptyMark;
    }
    return escapeHtml(String(rawValue));
  };

  async function downloadAnswersPdf() {
    if (!answerDetail) return;
    const info = getUserInfo(answerDetail);
    const title = activeForm?.title || "Form Response";
    const submitted = answerDetail.submittedAt
      ? formatDateTime(answerDetail.submittedAt)
      : "";

    const rows = normalizeAnswersList(answerDetail)
      .filter((ans) => {
        const fieldDef = findFieldDef(ans);
        const type = ans?.type || fieldDef?.type;
        return type !== "information";
      })
      .map((ans) => {
        const fieldDef = findFieldDef(ans);
        const label = ans?.label || fieldDef?.label || ans?.fieldId || "—";
        return `<tr>
          <th style="text-align:left;padding:8px 10px;border:1px solid #ddd;background:#f4f1ff;width:35%;vertical-align:top;font-weight:600;">${escapeHtml(label)}</th>
          <td style="padding:8px 10px;border:1px solid #ddd;vertical-align:top;word-break:break-word;">${renderAnswerValueAsHtml(ans, fieldDef)}</td>
        </tr>`;
      })
      .join("");

    const fontStack =
      '"Noto Sans Gujarati","Shruti","Nirmala UI","Mukti","Lohit Gujarati",Arial,Helvetica,sans-serif';

    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-10000px";
    container.style.top = "0";
    container.style.width = "794px"; // A4 @ 96dpi
    container.style.padding = "40px";
    container.style.background = "#fff";
    container.style.color = "#222";
    container.style.fontFamily = fontStack;
    container.style.fontSize = "13px";
    container.style.lineHeight = "1.45";

    container.innerHTML = `
      <h1 style="margin:0 0 6px 0;font-size:20px;font-family:${fontStack};">${escapeHtml(title)}</h1>
      <div style="margin-bottom:14px;">
        <div style="font-weight:700;margin-bottom:4px;">Submitted by</div>
        <div><strong>Name:</strong> ${escapeHtml(info.fullName)}</div>
        <div><strong>VUF ID:</strong> ${escapeHtml(info.vufId)}</div>
        <div><strong>Mobile:</strong> ${escapeHtml(info.mobile)}</div>
        ${submitted ? `<div style="color:#666;margin-top:4px;">Submitted: ${escapeHtml(submitted)}</div>` : ""}
      </div>
      <table style="border-collapse:collapse;width:100%;font-family:${fontStack};">
        <thead>
          <tr>
            <th style="text-align:left;padding:8px 10px;border:1px solid #ddd;background:#4a00e0;color:#fff;width:35%;">Question</th>
            <th style="text-align:left;padding:8px 10px;border:1px solid #ddd;background:#4a00e0;color:#fff;">Answer</th>
          </tr>
        </thead>
        <tbody>${rows || `<tr><td colspan="2" style="padding:10px;color:#888;">No answers.</td></tr>`}</tbody>
      </table>
    `;
    document.body.appendChild(container);

    try {
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#fff",
      });

      const imgData = canvas.toDataURL("image/png");
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position -= pageHeight;
        doc.addPage();
        doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const safeTitle =
        title.replace(/[^a-z0-9-_]+/gi, "_").slice(0, 60) || "form";
      const safeName =
        info.fullName.replace(/[^a-z0-9-_]+/gi, "_").slice(0, 40) || "response";
      doc.save(`${safeTitle}_${safeName}.pdf`);
    } catch (err) {
      toast.error(err?.message || "Failed to generate PDF");
    } finally {
      document.body.removeChild(container);
    }
  }

  const findFieldDef = (answer) => {
    const key =
      answer?.fieldId ||
      answer?.field_id ||
      answer?.id ||
      answer?.key ||
      answer?.name;
    if (!key || !Array.isArray(formFields)) return null;
    return (
      formFields.find((f) => f.id === key || f._id === key || f.name === key) ||
      null
    );
  };

  const renderAnswerValue = (answer, fieldDef) => {
    const type = answer?.type || fieldDef?.type;
    const inputType = answer?.inputType || fieldDef?.inputType;
    const options = answer?.options || fieldDef?.options || [];
    const rawValue = answer?.value !== undefined ? answer.value : answer?.answer;
    const empty = <span style={{ color: "#999" }}>---</span>;

    if (
      rawValue === null ||
      rawValue === undefined ||
      rawValue === "" ||
      (Array.isArray(rawValue) && rawValue.length === 0) ||
      (typeof rawValue === "object" &&
        !Array.isArray(rawValue) &&
        Object.keys(rawValue).length === 0)
    ) {
      return empty;
    }

    if (type === "selection") {
      const lookup = (v) => {
        const opt = options.find((o) => String(o.value) === String(v));
        return opt?.label ?? String(v);
      };
      if (Array.isArray(rawValue)) {
        return rawValue.map(lookup).join(", ");
      }
      return lookup(rawValue);
    }

    if (type === "boolean") {
      return rawValue ? "Yes" : "No";
    }

    if (type === "date") {
      const d = new Date(rawValue);
      return isNaN(d.getTime())
        ? String(rawValue)
        : d.toLocaleDateString();
    }

    if (type === "time") {
      const d = new Date(rawValue);
      if (!isNaN(d.getTime()) && String(rawValue).includes("T")) {
        return d.toLocaleTimeString();
      }
      return String(rawValue);
    }

    if (type === "image" || inputType === "image") {
      const items = Array.isArray(rawValue) ? rawValue : [rawValue];
      const urls = items.map((it) => extractUrl(it)).filter(Boolean);
      if (urls.length === 0) return empty;
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {urls.map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noreferrer">
              <img
                src={u}
                alt={answer?.label || fieldDef?.label || "image"}
                style={{
                  maxWidth: 180,
                  maxHeight: 180,
                  borderRadius: 8,
                  border: "1px solid #eee",
                }}
              />
            </a>
          ))}
        </div>
      );
    }

    if (type === "file") {
      const items = Array.isArray(rawValue) ? rawValue : [rawValue];
      const rendered = items
        .map((it, i) => {
          const u = extractUrl(it);
          const name = extractFileName(it);
          if (!u && !name) return null;
          if (u && isImageUrl(u)) {
            return (
              <div key={i} style={{ marginBottom: 4 }}>
                <a href={u} target="_blank" rel="noreferrer">
                  <img
                    src={u}
                    alt={name || "file"}
                    style={{
                      maxWidth: 180,
                      maxHeight: 180,
                      borderRadius: 8,
                      border: "1px solid #eee",
                    }}
                  />
                </a>
              </div>
            );
          }
          if (u) {
            return (
              <div key={i}>
                <a href={u} target="_blank" rel="noreferrer">
                  {name}
                </a>
              </div>
            );
          }
          return <div key={i}>{name}</div>;
        })
        .filter(Boolean);
      if (rendered.length === 0) return empty;
      return <div>{rendered}</div>;
    }

    if (type === "organization") {
      if (rawValue && typeof rawValue === "object" && !Array.isArray(rawValue)) {
        const rows = Object.entries(rawValue).filter(
          ([, v]) => v !== null && v !== undefined && v !== ""
        );
        if (rows.length === 0) return empty;
        return (
          <div>
            {rows.map(([k, v]) => (
              <div key={k} style={{ marginBottom: 2 }}>
                <strong>{prettyLabelFromKey(k)}:</strong> {String(v)}
              </div>
            ))}
          </div>
        );
      }
    }

    if (type === "multi_text" || Array.isArray(rawValue)) {
      const arr = Array.isArray(rawValue) ? rawValue : [rawValue];
      const parts = arr
        .map((v, i) => {
          if (v === null || v === undefined || v === "") return null;
          if (typeof v === "object") {
            const inner = Object.entries(v)
              .filter(([, vv]) => vv !== null && vv !== undefined && vv !== "")
              .map(([k, vv]) => (
                <span key={k} style={{ marginRight: 8 }}>
                  <strong>{prettyLabelFromKey(k)}:</strong> {String(vv)}
                </span>
              ));
            if (!inner.length) return null;
            return (
              <div key={i} style={{ marginBottom: 2 }}>
                {inner}
              </div>
            );
          }
          return (
            <div key={i} style={{ marginBottom: 2 }}>
              {String(v)}
            </div>
          );
        })
        .filter(Boolean);
      if (!parts.length) return empty;
      return <div>{parts}</div>;
    }

    if (typeof rawValue === "object") {
      const rows = Object.entries(rawValue).filter(
        ([, v]) => v !== null && v !== undefined && v !== ""
      );
      if (rows.length === 0) return empty;
      return (
        <div>
          {rows.map(([k, v]) => (
            <div key={k} style={{ marginBottom: 2 }}>
              <strong>{prettyLabelFromKey(k)}:</strong>{" "}
              {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </div>
          ))}
        </div>
      );
    }

    return String(rawValue);
  };

  const normalizeAnswersList = (detail) => {
    const answers = detail?.answers;
    if (!answers) return [];
    if (Array.isArray(answers)) return answers;
    // object keyed by fieldId → value
    return Object.entries(answers).map(([k, v]) => ({
      fieldId: k,
      value: v,
    }));
  };

  const statusBadgeStyle = (active) => ({
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    background: active ? "#28a745" : "#6c757d",
  });

  const formattedFormList = formList.map((form) => ({
    ...form,
    Status: (
      <span style={statusBadgeStyle(form.isActive)}>
        {form.isActive ? "Active" : "Inactive"}
      </span>
    ),
    Action: [
      <button
        key={`edit-${form.id}`}
        type="button"
        onClick={() => navigate(`/dynamic-form/${form.id}`)}
        className="btn btn-info btn-sm m-1"
        title="Edit"
      >
        <span
          className="menu-icon fas fa fa-edit"
          style={{ color: "white" }}
        ></span>
      </button>,
      <button
        key={`view-${form.id}`}
        type="button"
        onClick={() => openResponsesModal(form)}
        className="btn btn-primary btn-sm m-1"
        title="View Responses"
      >
        <span
          className="menu-icon fas fa fa-eye"
          style={{ color: "white" }}
        ></span>
      </button>,
      <button
        key={`download-${form.id}`}
        type="button"
        onClick={() => handleDownloadResponses(form.id, form.title)}
        className="btn btn-success btn-sm m-1"
        title="Download responses (Excel)"
        disabled={downloadingId === form.id}
      >
        <span
          className={`menu-icon fas fa ${
            downloadingId === form.id ? "fa-spinner fa-spin" : "fa-download"
          }`}
          style={{ color: "white" }}
        ></span>
      </button>,
    ],
  }));

  // const formattedFormList = formList.map((form) => ({
  //   ...form,
  //   Action: [
  //     <button
  //       key={`edit-${form.id}`}
  //       type="button"
  //       onClick={() => navigate(`/dynamic-form/${form.id}`)}
  //       className="btn btn-info btn-sm m-1"
  //       title="Edit"
  //     >
  //       <span
  //         className="menu-icon fas fa fa-edit"
  //         style={{ color: "white" }}
  //       ></span>
  //     </button>,
  //     <button
  //       key={`delete-${form.id}`}
  //       type="button"
  //       onClick={() => onDeleteForm(form.id)}
  //       className="btn btn-danger btn-sm m-1"
  //       title="Delete"
  //     >
  //       <span
  //         className="menu-icon fas fa fa-trash"
  //         style={{ color: "white" }}
  //       ></span>
  //     </button>,
  //   ],
  // }));

  const tableData = {
    columns: [
      { label: "Title", field: "title", sort: "asc", width: 22 },
      { label: "Description", field: "description", sort: "asc", width: 32 },
      { label: "Status", field: "Status", sort: "asc", width: 11 },
      { label: "Created At", field: "createdAt", sort: "asc", width: 20 },
      { label: "Action", field: "Action", width: 15 },
    ],
    rows: formattedFormList,
  };

  return (
    <React.Fragment>
      {isLoading ? (
        <Spinners />
      ) : (
        <Row>
          <Col xl="12">
            <Card>
              <CardHeader>
                <div className="row align-items-center">
                  <div className="col d-flex justify-content-between align-items-center">
                    <CardTitle className="h4 mb-0">Form List</CardTitle>
                    <Link
                      to="/dynamic-form"
                      className="btn btn-primary h5 mb-0"
                    >
                      <span className="menu-text text-white">
                        <i className="fa fa-plus me-2"></i>
                        Add Form
                      </span>
                    </Link>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <br />
                <div className="row">
                  <MDBDataTable
                    responsive
                    striped
                    bordered
                    data={tableData}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      <Modal
        isOpen={responsesModalOpen}
        toggle={closeResponsesModal}
        size="lg"
        centered
      >
        <ModalHeader toggle={closeResponsesModal}>
          {activeForm?.title
            ? `Responses — ${activeForm.title}`
            : "Responses"}
        </ModalHeader>
        <ModalBody>
          {responsesLoading ? (
            <div className="text-center py-4">
              <Spinners />
            </div>
          ) : responses.length === 0 ? (
            <p className="text-muted mb-0">No responses yet.</p>
          ) : (
            <ul className="list-group">
              {responses.map((r, idx) => (
                <li
                  key={getResponseId(r) || idx}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {getUserDisplayName(r)}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {formatDateTime(r.submittedAt)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openAnswersModal(r)}
                    className="btn btn-info btn-sm"
                    title="View Answers"
                  >
                    <i
                      className="fas fa-info-circle"
                      style={{ color: "white" }}
                    ></i>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={answersModalOpen}
        toggle={closeAnswersModal}
        size="lg"
        centered
      >
        <ModalHeader
          close={
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <button
                type="button"
                onClick={downloadAnswersPdf}
                className="btn btn-sm btn-outline-primary"
                title="Download form details as PDF"
                disabled={!answerDetail || answersLoading}
              >
                <i className="fas fa-download" />
              </button>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={closeAnswersModal}
              />
            </div>
          }
        >
          {answerDetail
            ? `Answers — ${getUserDisplayName(answerDetail)}`
            : "Answers"}
        </ModalHeader>
        <ModalBody>
          {answersLoading ? (
            <div className="text-center py-4">
              <Spinners />
            </div>
          ) : !answerDetail ? (
            <p className="text-muted mb-0">No data.</p>
          ) : (
            <div>
              {(() => {
                const info = getUserInfo(answerDetail);
                return (
                  <div
                    style={{
                      border: "1px solid #e6e6ef",
                      background: "#fafaff",
                      borderRadius: 8,
                      padding: "10px 14px",
                      marginBottom: 12,
                      fontSize: 13,
                    }}
                  >
                    <div>
                      <strong>Name:</strong> {info.fullName}
                    </div>
                    <div>
                      <strong>VUF ID:</strong> {info.vufId}
                    </div>
                    <div>
                      <strong>Mobile:</strong> {info.mobile}
                    </div>
                  </div>
                );
              })()}
              {answerDetail.submittedAt && (
                <p className="text-muted" style={{ fontSize: 12 }}>
                  Submitted: {formatDateTime(answerDetail.submittedAt)}
                </p>
              )}
              {normalizeAnswersList(answerDetail).length === 0 ? (
                <p className="text-muted mb-0">No answers found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered table-sm mb-0">
                    <tbody>
                      {normalizeAnswersList(answerDetail)
                        .filter((ans) => {
                          const fieldDef = findFieldDef(ans);
                          const type = ans?.type || fieldDef?.type;
                          return type !== "information";
                        })
                        .map((ans, idx) => {
                        const fieldDef = findFieldDef(ans);
                        const label =
                          ans?.label || fieldDef?.label || ans?.fieldId || "—";
                        return (
                          <tr key={ans?.fieldId || ans?.id || idx}>
                            <th
                              style={{ width: "35%", verticalAlign: "top" }}
                            >
                              {label}
                            </th>
                            <td style={{ wordBreak: "break-word" }}>
                              {renderAnswerValue(ans, fieldDef)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default connect(null, { setBreadcrumbItems })(FormList);
